import { createMemo } from 'solid-js';
import {
  useApiServices,
  useCapabilityRegistryContext,
  useDataContext,
  usePaneContext,
  createViewWithAssociations,
  getVisibleModelIds,
  getVisiblePointCloudIds,
  getVisibleTrajectoryIds,
} from '@mobile-mapping/providers';
import type {
  CreateViewFormData,
  CreateViewTabProps,
} from '@trimble/ui-components/solidjs';
import type { Markup } from '@data-access/connect/tc-tcps';
import type { Measurement } from '@trimble/web3d-providers';
import { useConnectProject } from '@trimble/connect-project-providers';
import { t } from '@trimble/i18n';
import { NotificationEventBus } from '@trimble/solidjs-notifications';
import { useLogger } from '@trimble/solidjs-utils';
import { asError } from '@trimble/logging';
import { getMultipaneReservedData } from '../views/get-multipane-reserved-data';

type CreateViewTabContext = Omit<CreateViewTabProps, 'onClose'>;

export function useCreateView(): CreateViewTabContext {
  const { tcTcpsApi, scanDataApi } = useApiServices();
  const { selectedRegion, selectedProject } = useConnectProject();
  const { dataState, activeItems } = useDataContext();
  const { capabilityRegistry } = useCapabilityRegistryContext();
  const { layouts } = usePaneContext();
  const logger = useLogger().getLogger('useCreateView');

  // Key snapshots by view id so overlapping saves cannot clobber label
  // correlation before getReservedData runs for its matching view.
  const snapshotsByViewId = new Map<
    string,
    { markups: readonly Markup[]; measurements: readonly Measurement[] }
  >();

  const hasModelsLoaded = createMemo(() => {
    const visibleModelIds = getVisibleModelIds(dataState);
    const visiblePointCloudIds = getVisiblePointCloudIds(dataState);
    const visibleTrajectoryIds = getVisibleTrajectoryIds(dataState);
    return (
      (visibleModelIds?.length ?? 0) > 0 ||
      (visiblePointCloudIds?.length ?? 0) > 0 ||
      (visibleTrajectoryIds?.length ?? 0) > 0
    );
  });

  const onSave = async (formData: CreateViewFormData): Promise<string> => {
    const region = selectedRegion();
    const project = selectedProject();
    if (!region || !project) {
      throw new Error('No region or project selected');
    }
    const activeLayout =
      layouts.layouts.find((l) => l.label === layouts.activeLayoutLabel) ??
      layouts.layouts[0];

    const { id, markups, measurements } = await createViewWithAssociations({
      name: formData.name,
      description: formData.description,
      tags: formData.tags,
      sharedWith: formData.sharedWith,
      region,
      project,
      dataState,
      capabilityRegistry,
      tcTcpsApi,
      scanDataApi,
      activeLayout,
    });
    snapshotsByViewId.set(id, { markups, measurements });
    return id;
  };

  return {
    onSave,
    hasModelsLoaded,
    tcTcpsApi,
    region: selectedRegion,
    projectId: () => selectedProject()?.id,
    reservedViewDataConfig: {
      appName: 'numark' as const,
      getReservedData: ({ viewId }) => {
        const snapshot = snapshotsByViewId.get(viewId);
        snapshotsByViewId.delete(viewId);
        return getMultipaneReservedData(
          capabilityRegistry,
          layouts,
          activeItems,
          dataState,
          snapshot?.markups ?? [],
          snapshot?.measurements ?? []
        );
      },
      onReservedDataError: (viewId: string, error: Error) => {
        logger.warn(
          `Multipane state save failed for view ${viewId}`,
          undefined,
          asError(error)
        );
        NotificationEventBus.emit({
          type: 'warning',
          message: t('mobileMapping.views.create.multipaneStateSaveFailed'),
        });
      },
    },
  };
}
